/**
 * BackendBuilder — paste a back-end quote, normalize it into the ElecBid format.
 *
 * Paste the raw back-end breakdown into one box and (optionally) the GPT prompt
 * you used into the other. "Convert" sends it to Claude, which transcribes it
 * faithfully (no inventing/adding) into the canonical ElecBid line structure and
 * computes the summary box — the same shape the Log Book import produces. Review
 * it, then "Save to ElecBid" to store it in the Quote Database (the GPT prompt is
 * kept as training data for the quote engine).
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../lib/apiFetch";
import { useLocation } from "react-router-dom";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4",
  amber: "#f59e0b", red: "#ef4444", blue: "#3b82f6",
};

function money(n) {
  if (n == null) return "—";
  return "$" + (+n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const boxStyle = {
  width: "100%", boxSizing: "border-box", background: C.surface,
  border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
  fontSize: 13, padding: "10px 14px", outline: "none",
  fontFamily: "'Roboto', sans-serif",
};

export default function BackendBuilder() {
  const loc = useLocation();
  const seed = loc.state || {};   // pre-fill when launched from a client / assessment
  const [backendText, setBackendText] = useState("");
  const [gptPrompt,   setGptPrompt]   = useState("");
  const [quoteNum,    setQuoteNum]    = useState(seed.quoteNumber || "");
  const [clientName,  setClientName]  = useState(seed.clientName || "");
  const [clientId,    setClientId]    = useState(seed.clientId || "");
  const [requestId,   setRequestId]   = useState(seed.jobberRequestId || "");
  const [assessmentId]                = useState(seed.assessmentId || "");
  const [cSearch,     setCSearch]     = useState("");
  const [cResults,    setCResults]    = useState([]);
  const [cSearching,  setCSearching]  = useState(false);
  const [result,      setResult]      = useState(null);   // { backend, lines, quoteTitle }
  const [busy,        setBusy]        = useState("");      // "" | "convert" | "save"
  const [msg,         setMsg]         = useState(null);
  const [err,         setErr]         = useState(false);
  // Client-facing draft
  const [cf,      setCf]      = useState(null);   // { title, options:[{name,scope,lineItems,total}] }
  const [cfRef,   setCfRef]   = useState(null);   // { backendId, quoteNumber }
  const [cfBusy,  setCfBusy]  = useState(false);
  const [cfInstr, setCfInstr] = useState("");

  const makeClientFacing = async (instructions) => {
    const ref = cfRef || (result?.backendId ? { backendId: result.backendId, quoteNumber: result.savedAs } : null);
    if (!ref) { setErr(true); setMsg("Save the back-end first, then make it client-facing."); return; }
    setCfBusy(true); setMsg(null); setErr(false);
    try {
      const res = await apiFetch("/api/quote-backends", {   // requireCrew-gated: sends the Supabase JWT
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "clientFacing", backendId: ref.backendId, quoteNumber: ref.quoteNumber, instructions: instructions || "" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setCf(data.draft); setCfRef({ backendId: data.backend.id, quoteNumber: data.backend.quote_num }); setCfInstr("");
    } catch (e) { setErr(true); setMsg("Client-facing error: " + e.message); }
    finally { setCfBusy(false); }
  };

  // Inline edits to the draft (so the contractor can tweak before pushing).
  const editOption = (oi, patch) => setCf(d => ({ ...d, options: d.options.map((o, i) => i === oi ? { ...o, ...patch } : o) }));
  const editItem   = (oi, li, patch) => setCf(d => ({ ...d, options: d.options.map((o, i) => i === oi ? { ...o, lineItems: o.lineItems.map((it, j) => j === li ? { ...it, ...patch } : it) } : o) }));

  // Client search → reuse the requests search (it carries the client object).
  const searchClients = useCallback(async (term) => {
    if (!term || term.trim().length < 2) { setCResults([]); return; }
    setCSearching(true);
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "searchRequests", searchTerm: term.trim() }),
      });
      const data = await res.json();
      const seen = new Set(); const out = [];
      for (const r of (data.requests || [])) {
        const c = r.client; if (!c?.id || seen.has(c.id)) continue; seen.add(c.id);
        const a = r.property?.address; const addr = a ? [a.street1, a.city].filter(Boolean).join(", ") : "";
        out.push({ id: c.id, name: c.name, requestId: r.id, address: addr });
      }
      setCResults(out.slice(0, 10));
    } catch { setCResults([]); } finally { setCSearching(false); }
  }, []);
  useEffect(() => { const t = setTimeout(() => searchClients(cSearch), 350); return () => clearTimeout(t); }, [cSearch, searchClients]);

  const pickClient = (c) => { setClientId(c.id); setClientName(c.name); setRequestId(c.requestId || ""); setCSearch(""); setCResults([]); };
  const clearClient = () => { setClientId(""); setClientName(""); setRequestId(""); };

  const call = async (dryRun) => {
    if (!backendText.trim()) { setErr(true); setMsg("Paste a back-end quote first."); return; }
    setBusy(dryRun ? "convert" : "save");
    setMsg(null); setErr(false);
    try {
      const res = await apiFetch("/api/quote-backends", {   // requireAdmin-gated
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "normalizePaste", backendText, gptPrompt, quoteNumber: quoteNum, clientName, jobberClientId: clientId, jobberRequestId: requestId, assessmentId, dryRun }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.details || "Request failed");
      setResult(data);
      if (!dryRun) setMsg(`Saved as #${data.savedAs} — it's now in the Quote Database.`);
    } catch (e) {
      setErr(true); setMsg("Error: " + e.message);
    } finally {
      setBusy("");
    }
  };

  const b = result?.backend;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px", color: C.text, fontFamily: "'Roboto', sans-serif" }}>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: ".04em", color: C.lime }}>
          Backend Builder
        </div>
        <div style={{ fontSize: 14, color: C.muted }}>
          Paste a back-end quote → it's transcribed into the ElecBid format (nothing added) and saved to the Quote Database.
        </div>
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12, alignItems: "flex-start" }}>
        <input style={{ ...boxStyle, flex: 1, minWidth: 140 }} placeholder="Quote # (optional)"
          value={quoteNum} onChange={e => setQuoteNum(e.target.value)} />

        {/* Client: search & attach, or free-type a name */}
        <div style={{ flex: 2, minWidth: 240, position: "relative" }}>
          {clientId ? (
            <div style={{ ...boxStyle, display: "flex", alignItems: "center", gap: 10, borderColor: "rgba(165,206,58,.5)" }}>
              <span style={{ color: C.lime, fontWeight: 700 }}>👤 {clientName}</span>
              <span style={{ fontSize: 11, color: C.muted }}>attached</span>
              <button onClick={clearClient} style={{ marginLeft: "auto", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 16, lineHeight: 1 }} title="Detach client">×</button>
            </div>
          ) : (
            <>
              <input style={boxStyle} placeholder="Search a client to attach…  (or type a name below)"
                value={cSearch} onChange={e => setCSearch(e.target.value)} />
              {(cSearching || cResults.length > 0) && (
                <div style={{ position: "absolute", zIndex: 5, top: 44, left: 0, right: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,.4)" }}>
                  {cSearching && <div style={{ padding: "9px 12px", fontSize: 12, color: C.muted }}>Searching…</div>}
                  {!cSearching && cResults.map(c => (
                    <div key={c.id} onClick={() => pickClient(c)}
                      style={{ padding: "9px 12px", cursor: "pointer", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(165,206,58,.06)"}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}>
                      <div style={{ color: C.text, fontWeight: 600 }}>{c.name}</div>
                      {c.address && <div style={{ color: C.muted, fontSize: 11 }}>{c.address}</div>}
                    </div>
                  ))}
                  {!cSearching && cResults.length === 0 && cSearch.trim().length >= 2 && (
                    <div style={{ padding: "9px 12px", fontSize: 12, color: C.muted }}>No clients found — you can type a name below.</div>
                  )}
                </div>
              )}
              <input style={{ ...boxStyle, marginTop: 8 }} placeholder="…or just type a client name (optional)"
                value={clientName} onChange={e => setClientName(e.target.value)} />
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".05em" }}>Back-end quote</div>
          <textarea style={{ ...boxStyle, height: 320, resize: "vertical", fontFamily: "monospace", lineHeight: 1.5 }}
            placeholder="Paste the back-end breakdown here — scope/labour hours, materials, costs, markups…"
            value={backendText} onChange={e => setBackendText(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".05em" }}>GPT prompt used (optional — helps training)</div>
          <textarea style={{ ...boxStyle, height: 320, resize: "vertical", lineHeight: 1.5 }}
            placeholder="Paste the prompt you gave GPT to produce this quote. Saved as training data — not used to change the numbers."
            value={gptPrompt} onChange={e => setGptPrompt(e.target.value)} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
        <button onClick={() => call(true)} disabled={!!busy}
          style={{ background: "rgba(165,206,58,.12)", border: `1px solid rgba(165,206,58,.4)`, borderRadius: 10, color: C.lime, fontWeight: 700, fontSize: 13, padding: "10px 22px", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "'Roboto', sans-serif" }}>
          {busy === "convert" ? "Converting…" : "Convert"}
        </button>
        {result && (
          <button onClick={() => call(false)} disabled={!!busy}
            style={{ background: C.lime, border: "none", borderRadius: 10, color: "#0d0f12", fontWeight: 700, fontSize: 13, padding: "10px 22px", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "'Roboto', sans-serif" }}>
            {busy === "save" ? "Saving…" : "Save to ElecBid"}
          </button>
        )}
      </div>

      {msg && (
        <div style={{ marginTop: 12, background: err ? "rgba(239,68,68,.08)" : "rgba(165,206,58,.08)", border: `1px solid ${err ? "rgba(239,68,68,.3)" : "rgba(165,206,58,.3)"}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: err ? C.red : C.lime }}>
          {msg}
        </div>
      )}

      {/* Preview of the normalized result */}
      {b && (
        <div style={{ marginTop: 18, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 22px" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: ".04em", color: C.lime, marginBottom: 4 }}>
            {result.quoteTitle || b.client_name || "Normalized back-end"}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
            #{b.quote_num}{b.client_name ? ` · ${b.client_name}` : ""} · {b.line_count} lines{result.dryRun ? " · preview (not saved yet)" : " · saved"}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            {[
              ["Van Hours", b.van_hours_quoted != null ? `${b.van_hours_quoted}h` : "—"],
              ["Labour $", money(b.hours_dollar_quoted)],
              ["Material/Permit $", money(b.expenses_cost_quoted)],
              ["Sub $", money(b.sub_contractor_cost_quoted)],
              ["Total Sell", money(b.total_sell)],
            ].map(([k, v]) => (
              <div key={k} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px" }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{k}</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{v}</div>
              </div>
            ))}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <tbody>
              {(result.lines || []).map((l, i) => l.is_header ? (
                <tr key={i}>
                  <td colSpan={5} style={{ padding: "8px 6px 3px", color: C.muted, fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: ".05em" }}>
                    {l.description}
                  </td>
                </tr>
              ) : (
                <tr key={i} style={{ borderBottom: "1px solid #1a1e26" }}>
                  <td style={{ padding: "4px 6px", width: 50, textAlign: "right", color: l.is_labour ? C.lime : C.text, fontWeight: 600, whiteSpace: "nowrap" }}>{l.qty ?? ""}</td>
                  <td style={{ padding: "4px 6px", width: 36, color: C.muted }}>{l.unit ?? ""}</td>
                  <td style={{ padding: "4px 6px", color: C.text }}>{l.description}</td>
                  <td style={{ padding: "4px 6px", width: 90, textAlign: "right", color: C.muted, whiteSpace: "nowrap" }}>{l.our_cost != null ? money(l.our_cost) : ""}</td>
                  <td style={{ padding: "4px 6px", width: 90, textAlign: "right", whiteSpace: "nowrap" }}>{l.client_total != null ? money(l.client_total) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Make it client-facing (available once the back-end is saved) */}
      {result?.savedAs && !cf && (
        <div style={{ marginTop: 14 }}>
          <button onClick={() => makeClientFacing("")} disabled={cfBusy}
            style={{ background: C.lime, border: "none", borderRadius: 10, color: "#0d0f12", fontWeight: 700, fontSize: 13, padding: "10px 22px", cursor: cfBusy ? "default" : "pointer", opacity: cfBusy ? 0.6 : 1 }}>
            {cfBusy ? "Generating…" : "Make Client-Facing →"}
          </button>
        </div>
      )}

      {cf && (
        <div style={{ marginTop: 18, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 22px" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: ".04em", color: C.lime, marginBottom: 4 }}>
            Client-Facing Quote
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>{cf.title}</div>

          {(cf.options || []).map((o, oi) => (
            <div key={oi} style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
              <input value={o.name || ""} onChange={e => editOption(oi, { name: e.target.value })}
                style={{ ...boxStyle, fontWeight: 700, fontSize: 15, color: C.lime, marginBottom: 8 }} />
              <textarea value={o.scope || ""} onChange={e => editOption(oi, { scope: e.target.value })}
                style={{ ...boxStyle, height: 150, resize: "vertical", lineHeight: 1.5, marginBottom: 10 }} />
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <tbody>
                  {(o.lineItems || []).map((it, li) => (
                    <tr key={li}>
                      <td style={{ padding: "3px 4px" }}>
                        <input value={it.name || ""} onChange={e => editItem(oi, li, { name: e.target.value })} style={{ ...boxStyle, padding: "5px 8px" }} />
                      </td>
                      <td style={{ padding: "3px 4px", width: 120 }}>
                        <input type="number" value={it.price ?? 0} onChange={e => editItem(oi, li, { price: parseFloat(e.target.value) || 0 })} style={{ ...boxStyle, padding: "5px 8px", textAlign: "right" }} />
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ padding: "8px 8px 0", textAlign: "right", fontWeight: 700 }}>Total</td>
                    <td style={{ padding: "8px 8px 0", textAlign: "right", fontWeight: 700, color: C.lime }}>{money(o.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
            <input value={cfInstr} onChange={e => setCfInstr(e.target.value)}
              placeholder="Tell it what to change — reword, split into 2 options, add a premium tier…"
              style={{ ...boxStyle, flex: 1, minWidth: 260 }}
              onKeyDown={e => { if (e.key === "Enter" && cfInstr.trim()) makeClientFacing(cfInstr); }} />
            <button onClick={() => makeClientFacing(cfInstr)} disabled={cfBusy || !cfInstr.trim()}
              style={{ background: "rgba(165,206,58,.12)", border: `1px solid rgba(165,206,58,.4)`, borderRadius: 10, color: C.lime, fontWeight: 700, fontSize: 13, padding: "10px 18px", cursor: (cfBusy || !cfInstr.trim()) ? "default" : "pointer", opacity: (cfBusy || !cfInstr.trim()) ? 0.6 : 1 }}>
              {cfBusy ? "Refining…" : "Refine"}
            </button>
            <button disabled title="Coming next — converts the client's open request into a Jobber quote with these options"
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted, fontWeight: 700, fontSize: 13, padding: "10px 18px", cursor: "default" }}>
              Push to Jobber (next)
            </button>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;600;700&display=swap');
        textarea::placeholder, input::placeholder { color: #5a6172; }
      `}</style>
    </div>
  );
}
